import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  Users,
  CreditCard,
  Gamepad2,
  Settings,
  FileText,
  BarChart3,
  Bell,
  LifeBuoy,
  Command,
} from 'lucide-react';
import { useUIStore } from '@/store/uiStore';
import { cn } from '@/lib/utils';

interface CommandItem {
  id: string;
  title: string;
  subtitle?: string;
  icon: React.ElementType;
  action: () => void;
  category: string;
}

export default function CommandPalette() {
  const navigate = useNavigate();
  const { toggleCommandPalette } = useUIStore();
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands: CommandItem[] = [
    // Navigation
    { id: 'dashboard', title: 'Go to Dashboard', icon: BarChart3, action: () => navigate('/'), category: 'Navigation' },
    { id: 'users', title: 'Go to Users', icon: Users, action: () => navigate('/users'), category: 'Navigation' },
    { id: 'transactions', title: 'Go to Transactions', icon: CreditCard, action: () => navigate('/transactions'), category: 'Navigation' },
    { id: 'games', title: 'Go to Games', icon: Gamepad2, action: () => navigate('/games'), category: 'Navigation' },
    { id: 'settings', title: 'Go to Settings', icon: Settings, action: () => navigate('/settings'), category: 'Navigation' },
    { id: 'reports', title: 'Go to Reports', icon: FileText, action: () => navigate('/reports/revenue'), category: 'Navigation' },
    { id: 'notifications', title: 'Go to Notifications', icon: Bell, action: () => navigate('/notifications'), category: 'Navigation' },
    { id: 'support', title: 'Go to Support', icon: LifeBuoy, action: () => navigate('/support'), category: 'Navigation' },
    
    // Quick Actions
    { id: 'search-users', title: 'Search Users', subtitle: 'Find user by phone or email', icon: Search, action: () => navigate('/users?search='), category: 'Quick Actions' },
    { id: 'pending-kyc', title: 'View Pending KYC', icon: Users, action: () => navigate('/kyc?status=pending'), category: 'Quick Actions' },
    { id: 'pending-withdrawals', title: 'View Pending Withdrawals', icon: CreditCard, action: () => navigate('/withdrawals?status=pending'), category: 'Quick Actions' },
    { id: 'active-games', title: 'View Active Games', icon: Gamepad2, action: () => navigate('/games?status=playing'), category: 'Quick Actions' },
  ];

  const filteredCommands = commands.filter((cmd) =>
    cmd.title.toLowerCase().includes(search.toLowerCase()) ||
    cmd.subtitle?.toLowerCase().includes(search.toLowerCase())
  );

  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {} as Record<string, CommandItem[]>);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, filteredCommands.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCommands[selectedIndex]) {
        filteredCommands[selectedIndex].action();
        toggleCommandPalette();
      }
    } else if (e.key === 'Escape') {
      toggleCommandPalette();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[20vh] px-4"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={toggleCommandPalette}
      />

      {/* Command palette */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -20 }}
        className="relative w-full max-w-xl bg-slate-900 rounded-2xl border border-slate-800 shadow-2xl overflow-hidden"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800">
          <Search className="w-5 h-5 text-slate-500" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search commands..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent text-white placeholder-slate-500 outline-none"
          />
          <kbd className="px-2 py-1 rounded bg-slate-800 text-slate-500 text-xs font-mono">ESC</kbd>
        </div>

        {/* Commands list */}
        <div className="max-h-[60vh] overflow-y-auto custom-scrollbar p-2">
          {Object.entries(groupedCommands).map(([category, items]) => (
            <div key={category} className="mb-4">
              <p className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {category}
              </p>
              {items.map((cmd) => {
                const index = filteredCommands.findIndex((c) => c.id === cmd.id);
                const isSelected = index === selectedIndex;
                
                return (
                  <button
                    key={cmd.id}
                    onClick={() => {
                      cmd.action();
                      toggleCommandPalette();
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                      isSelected
                        ? 'bg-violet-600/20 text-white'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                    )}
                  >
                    <div className={cn(
                      'w-8 h-8 rounded-lg flex items-center justify-center',
                      isSelected ? 'bg-violet-600' : 'bg-slate-800'
                    )}>
                      <cmd.icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-sm">{cmd.title}</p>
                      {cmd.subtitle && (
                        <p className="text-xs text-slate-500">{cmd.subtitle}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}

          {filteredCommands.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-slate-500">No commands found</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-slate-800 bg-slate-900/50">
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-slate-800">↑</kbd>
              <kbd className="px-1.5 py-0.5 rounded bg-slate-800">↓</kbd>
              to navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded bg-slate-800">↵</kbd>
              to select
            </span>
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Command className="w-3 h-3" />
            <span>K to open</span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
